/**
 * Compact inline line scroller — sits next to the team/side label in the
 * team cell of a SpreadsTotals row. Shows up to 3 chips at a time (prev,
 * selected, next) with chevron buttons; selected chip is highlighted.
 *
 * Chip order is the GameEventRow's data sort:
 *   - Spreads: signed line ascending (-X away-favored on the left,
 *     +X home-favored on the right)
 *   - Totals: line ascending
 *
 * The default-selected index (closest YES to 50¢) is computed upstream;
 * this component just renders whatever index is current.
 */

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/infra/utils';

export interface LineChipItem {
  key: string;
  label: string;            // formatted line value, e.g. "-3.5", "9.5"
  priceCents: number | null;
}

interface MiniLineStripProps {
  chips: LineChipItem[];
  selectedIdx: number;
  onSelect: (idx: number) => void;
  /**
   * #773 — chips whose market carries a current position, keyed by the chip's
   * own `key`. Working orders have their own row-level O indicator.
   *
   * OPT-IN, and undefined by default. This component has five call sites and
   * only one of them knows about own activity: the trade panel's own strip,
   * the price-chart line selector and the player-prop rung selector all render
   * chips that are not spread/total markets at all. Passing a mark set they
   * cannot populate would either do nothing or mark the wrong thing, so the
   * default has to be "no marks" — the same shape #651 used for `sideTint`.
   *
   * Keyed by the chip KEY (the market ticker), never by the line value or the
   * index: a spread ladder crosses zero, so two chips legitimately share a
   * magnitude, and the feed reorders the array under a mounted strip.
   */
  markedKeys?: ReadonlySet<string>;
  /** #648 — picking a line is a deliberate act on THIS card, so it may take a
   *  free hotkey focus. It must never steal focus from another card and never
   *  clear one: the chips stop propagation, so they never reach the row's
   *  focus-region handler, and the store call they use refuses both. This is
   *  the only way to focus a card without also expanding it. */
  onFocusCard?: () => void;
}

const VISIBLE = 3;

export function MiniLineStrip({
  chips,
  selectedIdx,
  onSelect,
  markedKeys,
  onFocusCard,
}: MiniLineStripProps) {
  if (chips.length === 0) return null;

  const select = (idx: number) => {
    onSelect(idx);
    onFocusCard?.();
  };

  // Window of `VISIBLE` chips centred on the selected index, clamped to
  // [0, chips.length).
  const half = Math.floor(VISIBLE / 2);
  let start = Math.max(0, selectedIdx - half);
  const end = Math.min(chips.length, start + VISIBLE);
  // Re-extend backwards if we hit the right edge so we still show VISIBLE.
  start = Math.max(0, end - VISIBLE);

  const canPrev = selectedIdx > 0;
  const canNext = selectedIdx < chips.length - 1;

  return (
    <div className="inline-flex items-center gap-0.5 font-mono text-[11px]">
      <button
        type="button"
        disabled={!canPrev}
        onClick={(e) => {
          e.stopPropagation();
          if (canPrev) select(selectedIdx - 1);
        }}
        className="text-muted-foreground hover:text-foreground rounded p-0.5 disabled:opacity-30"
        aria-label="Previous line"
      >
        <ChevronLeft size={12} />
      </button>
      {chips.slice(start, end).map((chip, i) => {
        const idx = start + i;
        const selected = idx === selectedIdx;
        const marked = markedKeys?.has(chip.key) ?? false;
        return (
          <button
            key={chip.key}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              select(idx);
            }}
            className={cn(
              'relative rounded px-1.5 py-0.5 transition-colors',
              selected
                ? 'text-foreground bg-primary/15 border border-primary/40'
                : 'text-muted-foreground hover:text-foreground border border-transparent',
            )}
            title={
              `Line ${chip.label}${chip.priceCents != null ? ` — ${chip.priceCents}¢ YES` : ''}` +
              `${marked ? '\nYou have an active position on this line' : ''}` +
              `\nticker: ${chip.key}`
            }
          >
            {chip.label}
            {marked && (
              <span
                // Absolutely positioned so the mark has NO layout box: the
                // strip sits in a 288px cell on a board already past its
                // column width at 1470, and a mark that reserved space would
                // widen every strip on the page whether marked or not.
                //
                // A dot, and only a dot. Selection already owns the border and
                // the background on this chip, and colour on this board
                // carries risk signals a trader reads (queue tone, the
                // fill-in-progress marker) — so the mark takes a channel
                // nothing else here uses rather than restating one.
                //
                // Position-only by contract. Working orders remain visible in
                // the separate O ring, so this cue has one stable meaning.
                className="bg-primary absolute -right-0.5 -top-0.5 h-1.5 w-1.5 rounded-full"
                data-activity-mark=""
                aria-hidden
              />
            )}
          </button>
        );
      })}
      <button
        type="button"
        disabled={!canNext}
        onClick={(e) => {
          e.stopPropagation();
          if (canNext) select(selectedIdx + 1);
        }}
        className="text-muted-foreground hover:text-foreground rounded p-0.5 disabled:opacity-30"
        aria-label="Next line"
      >
        <ChevronRight size={12} />
      </button>
    </div>
  );
}
